import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Skeleton from '@mui/material/Skeleton'
import LightModeIcon from '@mui/icons-material/LightMode'
import { styled } from '@mui/material/styles'

import { BoardingIcon } from 'asset/boarding_icon'
import { Types } from 'service-api'
import { useCallback } from 'react'

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.shade30,
  borderRadius: '30px',
  padding: '16px 24px',
  border: `1px solid ${theme.palette.primary.main}`,
  boxShadow: 'none',
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.shade30,
  },
}))

const availableServiceTypes = ['boarding', 'daycare']

interface TProps {
  isLoading: boolean
  services?: Types.LocationServiceTypeClientDto[] | Types.LocationServiceTypeAdminDto[]
  currentServiceType: string
  onSelect: (serviceType: string) => void
}

export const ServiceTypeSelector = ({ isLoading, services, currentServiceType, onSelect }: TProps) => {
  const onClickButton = useCallback(
    (ev: React.MouseEvent<HTMLButtonElement>) => {
      const { currentTarget: t } = ev
      onSelect(t.id)
    },
    [onSelect]
  )

  const renderPlaceholders = () => {
    return (
      <>
        <Skeleton width={130} height={54} sx={{ transform: 'scale(1,1)', borderRadius: '30px' }} />
        <Skeleton width={130} height={54} sx={{ transform: 'scale(1,1)', borderRadius: '30px' }} />
      </>
    )
  }

  const renderButtons = () => {
    return services?.map((st) => {
      if (availableServiceTypes.includes(st.name!)) {
        const buttonProps: GenericSimpleBag = {
          id: st.name!,
          startIcon: st.name === 'boarding' ? <BoardingIcon /> : <LightModeIcon />,
          variant: currentServiceType === st.name ? 'contained' : 'text',
          size: 'large',
          onClick: onClickButton,
        }

        if (currentServiceType === st.name) {
          return (
            <StyledButton key={st.id!} {...buttonProps}>
              {st.displayName!}
            </StyledButton>
          )
        } else {
          return (
            <Button key={st.id!} {...buttonProps} sx={{ px: 3, py: 2 }}>
              {st.displayName}
            </Button>
          )
        }
      }
      return null
    })
  }

  if (!isLoading && (services?.length ?? 0) < 2) {
    return null
  }

  return (
    <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
      {isLoading ? renderPlaceholders() : renderButtons()}
    </Stack>
  )
}
